/**
 * Agent Chat ```flow 블록 — 펜스를 읽어 공용 흐름도(components/flo)로 보인다.
 *
 * 말풍선 안에는 정적 썸네일만 두고, 그림을 누르면 크게 보기 창(같은 대화형 보기)을 연다.
 * 채팅 입력과 겹치지 않도록 키보드 동작은 두지 않는다 — 마우스 클릭으로만 연다.
 * 조작 안내(크게 보기·확대·강조)는 공용 컴포넌트가 늘 보인다 — 모델 답변에 맡기지 않는다.
 * 여기에 남는 챗 전용 일은 펜스 파싱·버전 확인·절단 안내 문구 만들기뿐이다.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { FLOW_FENCE_VERSION, fenceToGraph } from "@plannel/components/ai/flowFence";
import FloThumbnail from "@plannel/components/flo/FloThumbnail";
import FloDialog from "@plannel/components/flo/FloDialog";

/**
 * 펜스 본문(JSON)을 받아 그린다.
 * 읽을 수 없거나 옛 버전이면 그리지 않고, 새 버전이면 새로고침 안내만 낸다.
 */
export default function FlowBlock({ raw }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const spec = useMemo(() => {
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.warn("[FlowBlock] 흐름도 블록을 읽지 못했다", e);
      return null;
    }
  }, [raw]);
  const graph = useMemo(() => (spec && spec.v === FLOW_FENCE_VERSION ? fenceToGraph(spec) : null), [spec]);
  const title = useMemo(() => {
    const nodes = graph?.nodes || [];
    const roots = Array.from(new Set(nodes.filter((n) => n.root).map((n) => n.name)));
    return roots.length ? roots.join(", ") : (nodes[0]?.name || "");
  }, [graph]);

  if (!spec) return null;
  // 버전을 먼저 본다 — 새 형식은 노드 필드 모양이 달라도 새로고침 안내가 나가야 한다.
  if (spec.v > FLOW_FENCE_VERSION) {
    console.warn("[FlowBlock] 새 형식이라 그리지 않는다", spec.v);
    return (
      <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, my: 0.8 }}>
        {t("agentChatFlowNewer")}
      </Typography>
    );
  }
  if (spec.v !== FLOW_FENCE_VERSION) {
    console.warn("[FlowBlock] 읽지 않는 옛 스키마 버전", spec.v);
    return null;
  }
  if (!(spec.nodes || []).length) {
    console.warn("[FlowBlock] 그릴 노드가 없는 흐름도 블록");
    return null;
  }

  const cut = spec.m || null;
  const cutText = [
    cut && cut.total ? t("agentChatFlowCut", { shown: cut.shown, total: cut.total }) : "",
    cut && cut.full ? t("agentChatFlowDepthCut", { depth: cut.depth, full: cut.full }) : "",
  ].filter(Boolean).join(" · ");

  return (
    <Box sx={{ width: "100%", my: 0.8 }}>
      {/* 절단 안내는 썸네일 아래 줄 왼쪽, 크게 보기 안내는 같은 줄 오른쪽에 놓인다. */}
      <FloThumbnail graph={graph} caption={cutText} onClick={() => setOpen(true)} />
      {open && (
        <FloDialog open onClose={() => setOpen(false)} title={title} caption={cutText} graph={graph} />
      )}
    </Box>
  );
}
